import { useEffect, useState } from "react";
import { FileText, Download, ExternalLink, X, Loader2, AlertTriangle, FileType2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

/**
 * Sandbox-safe PDF viewer.
 * - Fetches the PDF as blob via axios (cookies included)
 * - Renders it inline in an <iframe> using the blob URL
 * - Provides user-initiated Download and Open-in-new-tab actions
 *
 * Props:
 *   title: header title
 *   subtitle: small subtitle (e.g. project name)
 *   filename: default filename for Download
 *   url: GET URL (optional - used if method === 'get' or undefined)
 *   method: 'get' | 'post'
 *   postBody: body for POST requests (optional)
 *   extraDownloads: optional list of {label, url, method, postBody, filename, mediaType}
 *                   for additional formats (e.g. Word docx)
 *   onClose: close handler
 */
export default function PdfPreviewModal({
  title = "PDF",
  subtitle = "",
  filename = "document.pdf",
  url,
  method = "get",
  postBody = null,
  extraDownloads = [],
  onClose,
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extraDownloading, setExtraDownloading] = useState(null);

  const handleExtraDownload = async (extra, mode = "download") => {
    setExtraDownloading(extra.label);
    try {
      const cfg = {
        withCredentials: true,
        responseType: "blob",
      };
      const res =
        (extra.method || "get") === "post"
          ? await axios.post(extra.url, extra.postBody || {}, cfg)
          : await axios.get(extra.url, cfg);
      const mt = extra.mediaType || res.headers["content-type"] || "application/octet-stream";
      const bu = window.URL.createObjectURL(new Blob([res.data], { type: mt }));
      if (mode === "open") {
        // Open the blob in a new tab — the browser will then offer to open
        // the .docx with the user's default application (Word, LibreOffice…).
        window.open(bu, "_blank", "noopener");
        setTimeout(() => window.URL.revokeObjectURL(bu), 60000);
      } else {
        const a = document.createElement("a");
        a.href = bu;
        a.download = extra.filename || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(bu), 4000);
      }
    } catch (err) {
      console.error("Extra download failed:", err);
      alert(`${extra.label}: Aktion fehlgeschlagen.`);
    }
    setExtraDownloading(null);
  };

  useEffect(() => {
    let revokeUrl = null;
    const fetchPdf = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = {
          withCredentials: true,
          responseType: "blob",
          timeout: 45000,
          // Don't auto-throw on 4xx — we want to read the JSON error body
          validateStatus: () => true,
        };
        const res =
          method === "post"
            ? await axios.post(url, postBody || {}, cfg)
            : await axios.get(url, cfg);
        if (res.status >= 400) {
          // Server returned an error response — extract the message from blob
          let detail = "";
          try {
            const text = await res.data.text();
            try {
              const j = JSON.parse(text);
              detail = j.detail || j.message || j.error || text.slice(0, 280);
            } catch {
              detail = text.slice(0, 280);
            }
          } catch {
            detail = res.statusText || "";
          }
          throw Object.assign(new Error("HTTP " + res.status), { status: res.status, detail });
        }
        const ct = res.headers["content-type"] || "application/pdf";
        if (!ct.includes("pdf") && !ct.includes("octet-stream")) {
          throw Object.assign(new Error("Unerwarteter Inhaltstyp: " + ct), { status: 0, detail: "Antwort war kein PDF." });
        }
        const blob = new Blob([res.data], { type: "application/pdf" });
        const bu = window.URL.createObjectURL(blob);
        revokeUrl = bu;
        setBlobUrl(bu);
      } catch (e) {
        console.error("PDF load failed:", e);
        const status = e?.status ?? e?.response?.status ?? 0;
        let msg;
        if (status === 401 || status === 403) {
          msg = "Nicht angemeldet oder kein Zugriff. Bitte ausloggen, neu einloggen und nochmals versuchen.";
        } else if (status >= 500) {
          msg = `Server-Fehler (${status}): ${e.detail || "Bitte später erneut versuchen."}`;
        } else if (status >= 400) {
          msg = `Anfrage abgelehnt (${status}): ${e.detail || ""}`;
        } else if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
          msg = "Zeitüberschreitung — der Server hat zu lange gebraucht. Bitte erneut versuchen.";
        } else {
          msg = `Netzwerkfehler: ${e?.message || "Verbindung fehlgeschlagen."} (Hinweis: Cookies / CORS prüfen)`;
        }
        setError(msg);
      }
      setLoading(false);
    };
    fetchPdf();
    return () => {
      if (revokeUrl) window.URL.revokeObjectURL(revokeUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, method]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.15 }}
        onClick={(e) => e.stopPropagation()}
        className="relative glass-surface rounded-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
        data-testid="pdf-preview-modal"
        style={{ border: "1px solid rgba(0,240,255,0.2)" }}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/5 bg-[#0A0A14]/95 backdrop-blur-xl flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-shrink">
            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-[#00F0FF]/10 flex-shrink-0">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#00F0FF]" />
            </div>
            <div className="min-w-0">
              <h2 className="font-['Outfit'] text-base sm:text-lg font-semibold text-white truncate">
                {title}
              </h2>
              {subtitle && (
                <p className="text-[10px] text-[#606070] font-['JetBrains_Mono'] truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink min-w-0">
            {blobUrl && (
              <>
                <a
                  data-testid="pdf-download-link"
                  href={blobUrl}
                  download={filename}
                  className="btn-primary flex items-center gap-2 text-xs py-2 px-3"
                >
                  <Download className="w-3.5 h-3.5" /> PDF herunterladen
                </a>
                <a
                  data-testid="pdf-open-tab-link"
                  href={blobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center gap-2 text-xs py-2 px-3"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Neuer Tab
                </a>
                {extraDownloads.map((ex) => {
                  const busy = extraDownloading === ex.label;
                  return (
                    <div key={ex.label} className="flex items-center gap-1">
                      <button
                        data-testid={`extra-open-${ex.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => handleExtraDownload(ex, "open")}
                        disabled={busy}
                        className="flex items-center gap-2 text-xs py-2 px-3 rounded-l-lg border-y border-l border-[#A855F7]/40 bg-[#A855F7]/15 text-[#A855F7] hover:bg-[#A855F7]/25 transition-colors font-['Outfit'] font-medium disabled:opacity-50"
                        title={`${ex.label} im neuen Tab öffnen (mit Word/LibreOffice öffnen)`}
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileType2 className="w-3.5 h-3.5" />}
                        {busy ? "Wird erzeugt…" : `${ex.label} öffnen`}
                      </button>
                      <button
                        data-testid={`extra-download-${ex.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => handleExtraDownload(ex, "download")}
                        disabled={busy}
                        className="flex items-center justify-center text-xs py-2 px-2 rounded-r-lg border-y border-r border-[#A855F7]/40 bg-[#A855F7]/10 text-[#A855F7] hover:bg-[#A855F7]/20 transition-colors disabled:opacity-50"
                        title={`${ex.label} herunterladen`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </>
            )}
            <button
              data-testid="pdf-preview-close"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              aria-label="Schliessen"
            >
              <X className="w-4 h-4 text-[#A0A0B0]" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 relative bg-[#05050A]">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
              <span className="text-xs text-[#A0A0B0] font-['JetBrains_Mono']">
                PDF wird erzeugt…
              </span>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6 sm:px-8">
              <div className="w-12 h-12 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
              </div>
              <span
                data-testid="pdf-error-message"
                className="text-sm text-[#FF3B30] font-['Outfit'] font-medium max-w-md break-words"
              >
                {error}
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                <button
                  data-testid="pdf-retry-btn"
                  onClick={() => window.location.reload()}
                  className="btn-secondary flex items-center gap-2 text-xs py-2 px-3"
                >
                  Seite neu laden
                </button>
                <button
                  data-testid="pdf-fallback-open"
                  onClick={() => {
                    // Fallback: open the URL directly in a new tab. The browser
                    // will send the request with cookies (same-origin) and
                    // render the PDF natively. Works even when XHR/CORS fails.
                    if (method === "get") {
                      window.open(url, "_blank", "noopener");
                      return;
                    }
                    // For POST endpoints: submit a hidden form to a new tab
                    // (form encodes JSON body as a single field; backend
                    // accepts empty body when preview3dImage is missing).
                    try {
                      const form = document.createElement("form");
                      form.method = "POST";
                      form.action = url;
                      form.target = "_blank";
                      form.enctype = "application/x-www-form-urlencoded";
                      form.style.display = "none";
                      document.body.appendChild(form);
                      form.submit();
                      setTimeout(() => form.remove(), 5000);
                    } catch (err) {
                      console.error("Fallback open failed:", err);
                    }
                  }}
                  className="btn-primary flex items-center gap-2 text-xs py-2 px-3"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Direkt im neuen Tab öffnen
                </button>
              </div>
              <p className="text-[10px] text-[#606070] font-['JetBrains_Mono'] mt-1 max-w-md">
                Wenn auch das nicht funktioniert: kurz ausloggen → neu einloggen → erneut versuchen. Falls weiterhin nicht, sende einen Screenshot dieser Meldung an den Support.
              </p>
            </div>
          )}
          {blobUrl && !error && (
            <iframe
              data-testid="pdf-preview-iframe"
              title={title}
              src={blobUrl}
              className="w-full h-full border-0"
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}
